import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useAuth } from '@clerk/clerk-react';
import { useProgressiveDataService, type Homeowner } from '../../lib/progressiveDataService';
import { SettingsCheckbox } from '../settings/SettingsCheckbox';

export default function ResidenceTab() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { updateHomeowner } = useProgressiveDataService();
  const [homeowner, setHomeowner] = useState<Homeowner | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    address: '',
    name: '',
    phone: '',
    sms_opt_in: false,
  });

  // CB:BEGIN load-residence-data-from-api
  // Load homeowner data on component mount from /api/users/me
  useEffect(() => {
    const loadHomeownerData = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      const clerkAddress = (user.publicMetadata as any)?.address as string | undefined;
      let lsAddress = '';
      try { lsAddress = localStorage.getItem('last_address') || ''; } catch {}

      // Helper to get first name only (or first part of full name)
      const getFirstName = (firstName?: string, lastName?: string, fullName?: string): string => {
        if (firstName) return firstName;
        if (fullName) {
          const parts = fullName.trim().split(' ');
          return parts[0] || '';
        }
        return '';
      };

      // Helper to get phone from multiple sources
      const getPhone = (dbPhone?: string, clerkPhone?: string): string => {
        return dbPhone || clerkPhone || '';
      };

      // Helper to get address from multiple sources
      const getAddress = (dbAddress?: string, clerkAddr?: string, lsAddr?: string): string => {
        return dbAddress || clerkAddr || lsAddr || '';
      };

      try {
        // Use /api/users/me which returns homeowner data directly
        const token = await getToken();
        if (!token) {
          throw new Error('Authentication required');
        }
        
        const { getApiUrl } = await import('../../lib/getApiUrl');
        const response = await fetch(getApiUrl('/api/users/me'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load user data: ${response.status}`);
        }

        const userData = await response.json();
        
        // Debug logging
        console.log('🔍 ResidenceTab: API response:', {
          hasAddress: !!userData.address,
          address: userData.address,
          hasFirstName: !!userData.firstName,
          firstName: userData.firstName,
          hasPhone: !!userData.phone,
          phone: userData.phone,
        });
        
        // Extract data from API response
        const dbAddress = userData.address || null;
        const dbFirstName = userData.firstName || null;
        const dbLastName = userData.lastName || null;
        const dbPhone = userData.phone || null; // Phone from User model
        
        // Also check /api/me for additional homeowner data (fallback)
        let additionalData = null;
        try {
          const meResponse = await fetch(getApiUrl('/api/me'), {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (meResponse.ok) {
            additionalData = await meResponse.json();
          }
        } catch (e) {
          // Ignore /api/me errors, use /api/users/me data
        }
        
        // Try to get address from projects if not in homeowner record
        let projectAddress = null;
        if (!dbAddress) {
          try {
            // Projects endpoint accepts homeowner_id (Clerk user ID)
            const projectsResponse = await fetch(getApiUrl(`/api/projects?homeowner_id=${user.id}`), {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
            if (projectsResponse.ok) {
              const projects = await projectsResponse.json();
              // Get address from most recent project
              if (Array.isArray(projects) && projects.length > 0) {
                const latestProject = projects.sort((a: any, b: any) => {
                  const aDate = a.createdAt || a.created_at || a.updatedAt || a.updated_at || 0;
                  const bDate = b.createdAt || b.created_at || b.updatedAt || b.updated_at || 0;
                  return new Date(bDate).getTime() - new Date(aDate).getTime();
                })[0];
                projectAddress = latestProject.address || null;
                console.log('🔍 ResidenceTab: Found address from project:', projectAddress);
              }
            }
          } catch (e) {
            console.log('ResidenceTab: Could not fetch projects for address:', e);
          }
        }
        
        const clerkPhone = user.phoneNumbers?.[0]?.phoneNumber || '';
        const finalAddress = getAddress(
          dbAddress || projectAddress || additionalData?.profile?.homeowner?.address,
          clerkAddress,
          lsAddress
        );
        const finalPhone = getPhone(dbPhone, clerkPhone);
        const finalName = getFirstName(
          dbFirstName || additionalData?.profile?.homeowner?.firstName,
          dbLastName || additionalData?.profile?.homeowner?.lastName,
          user.fullName || undefined
        );
        
        // Create homeowner object for state
        if (dbAddress || dbFirstName || dbLastName) {
          setHomeowner({
            id: userData.id || '',
            userId: userData.id,
            address: dbAddress || undefined,
            firstName: dbFirstName || undefined,
            lastName: dbLastName || undefined,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            user: dbPhone ? { id: userData.id || '', clerkId: user.id, email: userData.email || '', phone: dbPhone, role: 'HOMEOWNER' as const, created_at: new Date().toISOString(), updated_at: new Date().toISOString() } : undefined,
          });
        }
        
        setForm({
          address: finalAddress,
          name: finalName,
          phone: finalPhone,
          sms_opt_in: userData.notificationPrefs?.smsOptIn || false,
        });
        setError(null);
      } catch (err: any) {
        console.error('Error loading homeowner data:', err);
        // Fallback to Clerk data
        const clerkPhone = user.phoneNumbers?.[0]?.phoneNumber || '';
        const firstName = getFirstName(undefined, undefined, user.fullName || undefined);
        
        setForm({
          address: clerkAddress || lsAddress || '',
          name: firstName,
          phone: clerkPhone,
          sms_opt_in: false,
        });
        // Only show error if we can't even show the form
        if (!clerkAddress && !lsAddress && !user.fullName) {
          setError('Failed to load homeowner data. Please try refreshing the page.');
        } else {
          setError(null); // Form can still be used with Clerk data
        }
      } finally {
        setLoading(false);
      }
    };

    loadHomeownerData();
  }, [user?.id, getToken]);
  // CB:END load-residence-data-from-api

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setForm({ 
      ...form, 
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value 
    });
  };

  // CB:BEGIN save-residence-data-to-api
  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const { getApiUrl } = await import('../../lib/getApiUrl');
      
      // Parse name into first and last
      const nameParts = form.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Save to /api/users/me (includes homeowner and phone)
      const response = await fetch(getApiUrl('/api/users/me'), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
            body: JSON.stringify({
              firstName: firstName || undefined,
              lastName: lastName || undefined,
              address: form.address || undefined,
              phone: form.phone || undefined,
            }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save: ${response.status} ${errorText}`);
      }

      // Reload data to get updated values
      const updatedResponse = await fetch(getApiUrl('/api/users/me'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (updatedResponse.ok) {
        const updatedData = await updatedResponse.json();
        console.log('🔍 ResidenceTab: Reloaded data after save:', updatedData);
        
        setHomeowner({
          id: updatedData.id || '',
          userId: updatedData.id,
          address: updatedData.address || undefined,
          firstName: updatedData.firstName || undefined,
          lastName: updatedData.lastName || undefined,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          user: updatedData.phone ? { id: updatedData.id || '', clerkId: user.id, email: updatedData.email || '', phone: updatedData.phone, role: 'HOMEOWNER' as const, created_at: new Date().toISOString(), updated_at: new Date().toISOString() } : undefined,
        });
        
        // Update form with saved values - ensure we use the actual saved data
        const savedName = updatedData.firstName || (updatedData.firstName && updatedData.lastName ? `${updatedData.firstName} ${updatedData.lastName}`.trim() : '');
        setForm({
          address: updatedData.address || form.address,
          name: savedName || form.name,
          phone: updatedData.phone || form.phone,
          sms_opt_in: updatedData.notificationPrefs?.smsOptIn || form.sms_opt_in,
        });
      } else {
        // If reload fails, at least keep the form values we just saved
        console.log('⚠️ ResidenceTab: Could not reload data, keeping form values');
      }

      // Persist to localStorage as fallback
      try { localStorage.setItem('last_address', form.address); } catch {}

      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err: any) {
      console.error('Error updating homeowner data:', err);
      setError(err.message || 'Failed to save homeowner data');
    } finally {
      setSaving(false);
    }
  };
  // CB:END save-residence-data-to-api

  // CB:BEGIN clerk-modal-safe-wrapper
  // Stop Clerk modal from closing on clicks (bubble phase only, on tab root)
  const stopModalBubble = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  // CB:END clerk-modal-safe-wrapper

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>;
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
        {error}
        <br />
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div 
      onClick={stopModalBubble}
      className="corebids-settings"
      style={{ padding: '24px', maxWidth: '600px' }}
    >
      <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '24px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px', color: '#0f172a' }}>Residence Information</h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label htmlFor="address" style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#334155' }}>
              Address
            </label>
            <input
              id="address"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Enter your address"
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                border: '1px solid #cbd5e1', 
                borderRadius: '6px',
                fontSize: '14px',
                color: '#1e293b',
                background: 'white'
              }}
            />
          </div>

          <div>
            <label htmlFor="name" style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#334155' }}>
              Name
            </label>
            <input
              id="name"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter your name"
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                border: '1px solid #cbd5e1', 
                borderRadius: '6px',
                fontSize: '14px',
                color: '#1e293b',
                background: 'white'
              }}
            />
          </div>

          <div>
            <label htmlFor="phone" style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#334155' }}>
              Phone Number
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              placeholder="Enter your phone number"
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                border: '1px solid #cbd5e1', 
                borderRadius: '6px',
                fontSize: '14px',
                color: '#1e293b',
                background: 'white'
              }}
            />
          </div>


          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '12px',
              backgroundColor: saving ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              marginTop: '16px'
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>

          {success && (
            <div style={{ 
              padding: '12px 16px',
              background: '#f0fdf4',
              color: '#16a34a',
              borderRadius: '8px',
              fontSize: '14px',
              border: '1px solid #86efac',
              textAlign: 'center'
            }}>
              ✅ Changes saved successfully!
            </div>
          )}

          {error && (
            <div style={{ 
              padding: '12px 16px',
              background: '#fef2f2',
              color: '#dc2626',
              borderRadius: '8px',
              fontSize: '14px',
              border: '1px solid #fecaca'
            }}>
              {error}
            </div>
          )}
        </div>

        {homeowner && (
          <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>Current Information</h4>
            <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
              <div><strong>Address:</strong> {homeowner.address || form.address || 'Not set'}</div>
              <div><strong>Name:</strong> {homeowner.firstName || form.name || 'Not set'}</div>
              <div><strong>Phone:</strong> {homeowner.user?.phone || form.phone || 'Not set'}</div>
              <div><strong>Member since:</strong> {new Date(homeowner.created_at).toLocaleDateString()}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 